import type {ReactNode} from 'react'
import {ChevronRightIcon, AlertIcon} from '@primer/octicons-react'
import {clsx} from 'clsx'
import {WithShimmerEffect} from './WithShimmerEffect'
import styles from './ToolHeader.module.css'
import {StatusTextSlider} from './StatusTextSlider'

export interface ToolHeaderProps {
  /**
   * The leading visual element to display before the title (e.g., an icon).
   */
  leadingVisual?: ReactNode
  /**
   * The trailing visual element to display after the title (e.g., an indicator icon).
   */
  trailingVisual?: ReactNode
  /**
   * Indicates whether the body the header controls is collapsed.
   */
  isCollapsed?: boolean
  /**
   * Title text for the tool header.
   */
  title: string
  /**
   * When true, the title is wrapped in a shimmer effect to indicate a pending/loading state.
   */
  isPending?: boolean
  /**
   * Optional path text rendered next to the title (e.g. a file path).
   */
  path?: string
  /**
   * Optional suffix rendered after the title and path (e.g., a duration indicator).
   */
  titleSuffix?: ReactNode
  /**
   * Renders the title suffix inside the wrapping text container.
   */
  inlineTitleSuffix?: boolean
  /**
   * Indicates whether the tool has references rendered as children. When false, the header
   * becomes non-interactive (no toggle, no chevron).
   */
  hasReferences?: boolean
  /**
   * Indicates whether the tool is in an error state.
   */
  isError?: boolean
  /**
   * Indicates whether the tool is part of a group.
   */
  isInGroup?: boolean
  /**
   * Indicates whether this header is for a group (not a child inside a group).
   */
  isGroupHeader?: boolean
  /**
   * Muted status text shown inline after the chevron (e.g., current tool call name).
   */
  statusText?: string
  /**
   * Callback for handling modifier + click. The boolean argument is `true` when the request
   * is to expand all (i.e. the tool is currently collapsed), `false` to collapse all.
   */
  onModifierClick?: (expand: boolean) => void
  /**
   * Callback for handling regular click to toggle this tool.
   */
  onToggle?: () => void
}

/**
 * Header chrome for a tool invocation. Pure UI: no context, no analytics, no data fetching.
 *
 * Callers are responsible for:
 * - Wiring analytics (wrap `onToggle` / `onModifierClick`)
 * - Choosing leading/trailing visuals (e.g. tool icons, indicators)
 */
export function ToolHeader({
  leadingVisual,
  trailingVisual,
  title,
  isPending = false,
  path,
  titleSuffix,
  inlineTitleSuffix = false,
  isCollapsed,
  hasReferences = true,
  isError = false,
  isInGroup = false,
  isGroupHeader = false,
  statusText,
  onModifierClick,
  onToggle,
}: ToolHeaderProps) {
  const handleClick = (event: React.MouseEvent) => {
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    if ((event.altKey || event.metaKey) && onModifierClick) {
      event.preventDefault()
      event.stopPropagation()
      // If currently collapsed, expand all; if expanded, collapse all
      onModifierClick(!!isCollapsed)
      return false
    }
    onToggle?.()
  }

  const titleNode = <span className={clsx(styles.title, {[styles.titleInGroup]: isInGroup})}>{title}</span>
  const wrappedTitle = isPending ? (
    <WithShimmerEffect className={styles.shimmerInline} innerClassName={styles.shimmerInline}>
      {titleNode}
    </WithShimmerEffect>
  ) : (
    titleNode
  )

  return (
    <div className={styles.headerContainer}>
      <button
        type="button"
        className={clsx(styles.wrapper, {
          [styles.inGroup]: isInGroup,
          [styles.groupHeader]: isGroupHeader,
          [styles.nonInteractive]: !hasReferences,
        })}
        onClick={hasReferences ? handleClick : undefined}
        aria-disabled={!hasReferences}
        aria-expanded={hasReferences ? !isCollapsed : undefined}
        tabIndex={hasReferences ? 0 : -1}
        data-testid="tool-header"
        data-has-children={hasReferences}
        data-is-pending={isPending}
      >
        {leadingVisual ? (
          <span className={styles.leadingVisualContainer}>
            <span className={styles.leadingVisualIcon}>{leadingVisual}</span>
            {hasReferences && (
              <ChevronRightIcon
                size={12}
                className={styles.leadingVisualChevron}
                data-expanded={!isCollapsed}
                aria-hidden="true"
              />
            )}
          </span>
        ) : null}
        <div className={styles.text}>
          {wrappedTitle}
          {path && (
            <>
              {' '}
              <span className={styles.path}>{path}</span>
            </>
          )}
          {inlineTitleSuffix && titleSuffix ? <> {titleSuffix}</> : null}
        </div>
        {!inlineTitleSuffix && titleSuffix}
        {hasReferences && !leadingVisual && (
          <ChevronRightIcon size={12} className={styles.toggleIcon} data-expanded={!isCollapsed} aria-hidden="true" />
        )}
        {statusText && <StatusTextSlider text={statusText} />}
        {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
        {isError && <AlertIcon size={16} className="color-fg-attention shrink-0" aria-label="Error" />}
      </button>
      {trailingVisual ? <div className={styles.trailingVisualContainer}>{trailingVisual}</div> : null}
    </div>
  )
}
